import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { OptimizedImage } from '../ui/optimized-image';
import { 
  User, 
  Edit, 
  Save, 
  X, 
  Upload, 
  Phone, 
  Mail, 
  MapPin, 
  Users, 
  Plus,
  Trash2,
  Camera,
  Facebook,
  Linkedin,
  Instagram,
  Calendar
} from 'lucide-react';

const ProfileManagement = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [newFamilyMember, setNewFamilyMember] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        
        // Vérifier si le nom (first_name et last_name) est invalide (vide, null, ou "-")
        const isInvalidName = (name) => {
          return !name || name.trim() === '' || name.trim() === '-' || name.trim() === 'undefined';
        };
        
        if (isInvalidName(userData.first_name) || isInvalidName(userData.last_name)) {
          console.log('Données utilisateur invalides, déconnexion...');
          // Nettoyer le localStorage
          localStorage.removeItem('authToken');
          localStorage.removeItem('memberToken');
          localStorage.removeItem('currentUser');
          localStorage.removeItem('memberEmail');
          localStorage.removeItem('memberFullName');
          localStorage.setItem('sessionExpiredMessage', 'Vos données de session sont incomplètes. Veuillez vous reconnecter.');
          // Rediriger vers le login
          window.location.href = '/membre';
          return;
        }
        
        setUser(userData);
        setFormData({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          address: userData.address || '',
          city: userData.city || '',
          postal_code: userData.postal_code || '',
          country: userData.country || 'Belgique',
          birth_date: userData.birth_date || userData.date_naissance || '',
          facebook: userData.facebook || '',
          linkedin: userData.linkedin || '',
          instagram: userData.instagram || '',
          share_email: userData.share_email === true,
          share_phone: userData.share_phone === true,
          share_address: userData.share_address === true,
          share_social: userData.share_social === true
        });
        console.log('📊 Préférences de partage chargées:', {
          share_email: userData.share_email,
          share_phone: userData.share_phone,
          share_address: userData.share_address
        });
        setFamilyMembers(userData.family_members || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleShare = async (field, value) => {
    console.log(`🔄 Toggle ${field}: ${!value} → ${value}`);
    
    // Mettre à jour immédiatement l'état local
    setFormData({...formData, [field]: value});
    
    // Sauvegarder automatiquement dans la base de données
    try {
      const token = localStorage.getItem('memberToken') || localStorage.getItem('authToken') || localStorage.getItem('token');
      console.log('Token trouvé:', token ? 'Oui' : 'Non');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/profile/extended`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          [field]: value
        })
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        console.log(`✅ ${field} sauvegardé: ${value}`);
      } else {
        // En cas d'erreur, revenir à l'ancien état
        console.error('❌ Erreur sauvegarde, rollback');
        setFormData({...formData, [field]: !value});
        alert('Erreur lors de la mise à jour des préférences de partage');
      }
    } catch (error) {
      console.error('❌ Erreur mise à jour partage:', error);
      // En cas d'erreur, revenir à l'ancien état
      setFormData({...formData, [field]: !value});
      alert('Erreur lors de la mise à jour des préférences de partage');
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('memberToken');
      
      let profile_picture_url = user?.profile_picture;
      
      // Uploader la photo si elle a été changée
      if (profilePicture && profilePicture.startsWith('data:')) {
        const formData = new FormData();
        
        // Convertir base64 en blob
        const response = await fetch(profilePicture);
        const blob = await response.blob();
        formData.append('file', blob, 'profile_picture.jpg');
        
        // Uploader l'image
        const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/upload-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          profile_picture_url = uploadData.url || uploadData.file_url;
        } else {
          const errorText = await uploadResponse.text();
          console.error('Erreur upload:', uploadResponse.status, errorText);
          alert('Erreur lors de l\'upload de la photo: ' + errorText);
          return;
        }
      }
      
      // Sauvegarder le profil avec l'URL de la photo
      const updateResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/profile/extended`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          family_members: familyMembers,
          profile_picture: profile_picture_url
        }),
      });

      if (updateResponse.ok) {
        const updatedUser = await updateResponse.json();
        setUser(updatedUser);
        // Recharger formData avec les données mises à jour incluant les préférences de partage
        setFormData({
          first_name: updatedUser.first_name || '',
          last_name: updatedUser.last_name || '',
          email: updatedUser.email || '',
          phone: updatedUser.phone || '',
          address: updatedUser.address || '',
          city: updatedUser.city || '',
          postal_code: updatedUser.postal_code || '',
          country: updatedUser.country || 'Belgique',
          birth_date: updatedUser.birth_date || updatedUser.date_naissance || '',
          share_email: updatedUser.share_email || false,
          share_phone: updatedUser.share_phone || false,
          share_address: updatedUser.share_address || false
        });
        setProfilePicture(null); // Réinitialiser l'aperçu
        setIsEditing(false);
        alert('Profil sauvegardé avec succès');
      } else {
        const errorText = await updateResponse.text();
        console.error('Erreur save profil:', updateResponse.status, errorText);
        alert('Erreur lors de la sauvegarde: ' + errorText);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde: ' + error.message);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: user?.address || '',
      city: user?.city || '',
      postal_code: user?.postal_code || '',
      country: user?.country || 'Belgique',
      birth_date: user?.birth_date || user?.date_naissance || '',
      share_email: user?.share_email || false,
      share_phone: user?.share_phone || false,
      share_address: user?.share_address || false
    });
    setFamilyMembers(user?.family_members || []);
    setIsEditing(false);
  };

  const addFamilyMember = () => {
    if (newFamilyMember.trim()) {
      setFamilyMembers([...familyMembers, newFamilyMember.trim()]);
      setNewFamilyMember('');
    }
  };

  const removeFamilyMember = (index) => {
    const updated = familyMembers.filter((_, i) => i !== index);
    setFamilyMembers(updated);
  };


  const handleChangePassword = async () => {
    console.log('🔒 Changement de mot de passe - Début');
    console.log('passwordData:', passwordData);
    
    // Validation
    if (!passwordData.new_password || !passwordData.confirm_password) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.new_password.length < 8) {
      alert('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    console.log('✅ Validations passées, envoi de la requête...');
    
    try {
      const token = localStorage.getItem('memberToken') || localStorage.getItem('authToken') || localStorage.getItem('token');
      console.log('Token trouvé:', token ? 'Oui' : 'Non');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_password: passwordData.new_password
        })
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        alert('Mot de passe changé avec succès !');
        setShowPasswordModal(false);
        setPasswordData({
          new_password: '',
          confirm_password: ''
        });
      } else {
        const error = await response.json();
        console.error('Erreur API:', error);
        alert(error.detail || 'Erreur lors du changement de mot de passe');
      }
    } catch (error) {
      console.error('Erreur changement mot de passe:', error);
      alert('Erreur lors du changement de mot de passe');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image ne doit pas dépasser 5MB');
      return;
    }

    // Convertir en base64 pour l'aperçu
    const reader = new FileReader();
    reader.onload = () => {
      setProfilePicture(reader.result);
    };
    reader.readAsDataURL(file);

    // TODO: Uploader vers le serveur
    // const formData = new FormData();
    // formData.append('profile_picture', file);
    // await uploadProfilePicture(formData);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrateur';
      case 'membre_vie': return 'Membre à vie';
      case 'membre_annuel': return 'Membre annuel';
      case 'membre_exterieur': return 'Membre extérieur';
      case 'decede': return 'Décédé';
      case 'prospect': return 'Prospect';
      case 'non_actif': return 'Non-actif';
      default: return role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'membre_vie': return 'bg-green-100 text-green-800 border-green-200';
      case 'membre_annuel': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'membre_exterieur': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'decede': return 'bg-slate-200 text-slate-700 border-slate-300';
      case 'prospect': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'non_actif': return 'bg-zinc-200 text-zinc-700 border-zinc-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Chargement du profil...</div>
      </div>
    );
  }

  // Vérification des données AVANT le rendu - si invalides, rediriger vers login
  const isInvalidData = (value) => {
    return !value || value.trim() === '' || value.trim() === '-' || value === 'undefined' || value === 'null';
  };

  if (isInvalidData(user?.first_name) || isInvalidData(user?.last_name) || isInvalidData(user?.email)) {
    // Nettoyer le localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('memberToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('memberEmail');
    localStorage.removeItem('memberFullName');
    localStorage.setItem('sessionExpiredMessage', 'Vos données de session sont incomplètes. Veuillez vous reconnecter.');
    // Rediriger vers le login
    window.location.href = '/membre';
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 
          className="text-3xl font-bold" 
          style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
        >
          Mon Profil
        </h1>
        {!isEditing ? (
          <Button 
            onClick={() => setIsEditing(true)}
            className="text-white"
            style={{ backgroundColor: 'rgb(196, 150, 96)' }}
          >
            <Edit className="w-4 h-4 mr-2" />
            Modifier
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button 
              onClick={handleSave}
              className="text-white"
              style={{ backgroundColor: 'rgb(196, 150, 96)' }}
            >
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
          </div>
        )}
      </div>

      {/* Note de confidentialité */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800" style={{ fontFamily: '"Lora", serif' }}>
          <strong>🔒 Protection de vos données :</strong> Toutes les informations renseignées sur cette page seront strictement utilisées dans le cadre des activités des Lignages de Bruxelles et ne seront jamais communiquées à des tiers.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Photo de profil et statut */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
              Photo de profil
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="relative inline-block">
              <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center mx-auto overflow-hidden">
                {profilePicture || user?.profile_picture ? (
                  <OptimizedImage 
                    src={profilePicture || (user?.profile_picture?.startsWith('http') ? user.profile_picture : `${process.env.REACT_APP_BACKEND_URL}${user.profile_picture}`)} 
                    alt="Photo de profil"
                    className="w-full h-full object-cover"
                    useThumbnail={true}
                  />
                ) : (
                  <User className="w-16 h-16 text-gray-400" />
                )}
              </div>
              {isEditing && (
                <label className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-50">
                  <Camera className="w-4 h-4" style={{ color: 'rgb(196, 150, 96)' }} />
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleFileUpload}
                  />
                </label>
              )}
            </div>
            
            <div>
              <h3 className="text-xl font-semibold">
                {user?.first_name} {user?.last_name}
              </h3>
              <span 
                className={`inline-block px-3 py-1 rounded-full text-sm font-medium border mt-2 ${getRoleBadgeColor(user?.role)}`}
              >
                {getRoleLabel(user?.role)}
              </span>
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex items-center justify-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${user?.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span>{user?.is_active ? 'Compte actif' : 'Compte inactif'}</span>
              </div>
              <div>
                Membre depuis {new Date(user?.created_at).toLocaleDateString('fr-FR')}
              </div>
              {user?.last_login && (
                <div>
                  Dernière connexion: {new Date(user?.last_login).toLocaleDateString('fr-FR')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Informations personnelles */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
              Informations personnelles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Prénom *
                </label>
                <p className="text-gray-900 py-2 bg-gray-50 px-3 rounded border border-gray-200">{user?.first_name || '-'}</p>
                <p className="text-xs text-gray-500 mt-1">Non modifiable</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Nom *
                </label>
                <p className="text-gray-900 py-2 bg-gray-50 px-3 rounded border border-gray-200">{user?.last_name || '-'}</p>
                <p className="text-xs text-gray-500 mt-1">Non modifiable</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email *
                </label>
                {isEditing ? (
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="votre@email.com"
                    required
                  />
                ) : (
                  <p className="text-gray-900 py-2">{user?.email || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Téléphone
                </label>
                {isEditing ? (
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+32 xxx xx xx xx"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{user?.phone || '-'}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date de naissance
              </label>
              {isEditing ? (
                <Input
                  type="date"
                  value={formData.birth_date || ''}
                  onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                />
              ) : (
                <p className="text-gray-900 py-2">
                  {(user?.birth_date || user?.date_naissance) ? new Date(user.birth_date || user.date_naissance).toLocaleDateString('fr-FR') : '-'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Adresse
              </label>
              {isEditing ? (
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Votre adresse complète"
                />
              ) : (
                <p className="text-gray-900 py-2">{user?.address || '-'}</p>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                {isEditing ? (
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="Bruxelles"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{user?.city || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                {isEditing ? (
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                    placeholder="1000"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{user?.postal_code || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
                {isEditing ? (
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    placeholder="Belgique"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{user?.country || '-'}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Réseaux sociaux */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Réseaux sociaux
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Ajoutez vos profils de réseaux sociaux pour faciliter les contacts entre membres.
          </p>
          
          <div className="grid md:grid-cols-1 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <Facebook className="w-4 h-4 text-blue-600" />
                Facebook
              </label>
              {isEditing ? (
                <Input
                  value={formData.facebook}
                  onChange={(e) => setFormData({...formData, facebook: e.target.value})}
                  placeholder="https://facebook.com/votre-profil"
                />
              ) : (
                <p className="text-gray-900 py-2">{user?.facebook || '-'}</p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <Linkedin className="w-4 h-4 text-blue-700" />
                LinkedIn
              </label>
              {isEditing ? (
                <Input
                  value={formData.linkedin}
                  onChange={(e) => setFormData({...formData, linkedin: e.target.value})}
                  placeholder="https://linkedin.com/in/votre-profil"
                />
              ) : (
                <p className="text-gray-900 py-2">{user?.linkedin || '-'}</p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <Instagram className="w-4 h-4 text-pink-600" />
                Instagram
              </label>
              {isEditing ? (
                <Input
                  value={formData.instagram}
                  onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                  placeholder="https://instagram.com/votre-profil"
                />
              ) : (
                <p className="text-gray-900 py-2">{user?.instagram || '-'}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Préférences de partage */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Partage de coordonnées avec les autres membres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Choisissez quelles informations vous souhaitez partager avec les autres membres dans la page "Contacts".
            Votre nom, prénom et photo de profil seront toujours visibles.
          </p>
          
          <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Mail className="w-4 h-4" />
                Adresse email
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.share_email || false}
                  onChange={(e) => handleToggleShare('share_email', e.target.checked)}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 rounded-full transition-all
                  ${formData.share_email ? 'bg-blue-600' : 'bg-gray-200'}
                  peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}
                ></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Phone className="w-4 h-4" />
                Numéro de téléphone
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.share_phone || false}
                  onChange={(e) => handleToggleShare('share_phone', e.target.checked)}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 rounded-full transition-all
                  ${formData.share_phone ? 'bg-blue-600' : 'bg-gray-200'}
                  peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}
                ></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <MapPin className="w-4 h-4" />
                Adresse postale
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.share_address || false}
                  onChange={(e) => handleToggleShare('share_address', e.target.checked)}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 rounded-full transition-all
                  ${formData.share_address ? 'bg-blue-600' : 'bg-gray-200'}
                  peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}
                ></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Users className="w-4 h-4" />
                Réseaux sociaux
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.share_social || false}
                  onChange={(e) => handleToggleShare('share_social', e.target.checked)}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 rounded-full transition-all
                  ${formData.share_social ? 'bg-blue-600' : 'bg-gray-200'}
                  peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}
                ></div>
              </label>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-3">
            💡 Les modifications de partage sont enregistrées automatiquement.
          </p>
        </CardContent>
      </Card>

      {/* Membres de la famille */}
      <Card>
        <CardHeader>
          <CardTitle 
            className="flex items-center"
            style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
          >
            <Users className="w-5 h-5 mr-2" />
            Membres de la famille
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            Mentionnez les autres membres de votre famille qui font partie des lignages.
          </p>
          
          <div className="space-y-3">
            {familyMembers.map((member, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-900">{member}</span>
                {isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeFamilyMember(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}

            {familyMembers.length === 0 && (
              <p className="text-gray-500 italic">Aucun membre de famille mentionné</p>
            )}

            {isEditing && (
              <div className="flex space-x-2">
                <Input
                  value={newFamilyMember}
                  onChange={(e) => setNewFamilyMember(e.target.value)}
                  placeholder="Nom du membre de famille"
                  onKeyPress={(e) => e.key === 'Enter' && addFamilyMember()}
                />
                <Button 
                  onClick={addFamilyMember}
                  className="text-white"
                  style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Informations de sécurité */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
            Sécurité du compte
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">Mot de passe</h4>
              <p className="text-sm text-gray-600 mb-3">
                Dernière modification: {user?.updated_at ? new Date(user?.updated_at).toLocaleDateString('fr-FR') : 'Jamais'}
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowPasswordModal(true)}
              >
                Changer le mot de passe
              </Button>
            </div>
            <div>
              <h4 className="font-medium mb-2">Tentatives de connexion</h4>
              <p className="text-sm text-gray-600">
                Tentatives échouées: {user?.failed_login_attempts || 0}
              </p>
              {user?.account_locked_until && (
                <p className="text-sm text-red-600">
                  Compte verrouillé jusqu'au: {new Date(user?.account_locked_until).toLocaleString('fr-FR')}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de changement de mot de passe */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">Changer le mot de passe</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nouveau mot de passe *</label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  className="w-full border rounded p-2"
                  placeholder="Au moins 8 caractères"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Confirmer le nouveau mot de passe *</label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  className="w-full border rounded p-2"
                  placeholder="Retapez le nouveau mot de passe"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleChangePassword}
                className="flex-1"
              >
                Confirmer
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ new_password: '', confirm_password: '' });
                }}
                className="flex-1"
              >
                Annuler
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileManagement;